import React, { useState } from 'react'
import { Col, Radio, Button, Form } from 'antd'
import axios from 'axios'
import FedexFreightAccountFieldset from './FedexFreightAccountFieldset'

// Replaces the old single "FedEx Freight Account Number" input in the Location modal
// (WarehouseComponent.js) with: a third-party-vs-location-specific choice, and a Standard
// LTL Freight sub-account and a separate Freight Direct sub-account (each with its own
// Test Connection call to /test_location_fedex_account).
const REQUIRED_TEST_FIELDS = [
  ['address', 'Address is required'],
  ['city', 'City is required'],
  ['state', 'State/Province is required'],
  ['zip', 'ZIP/Postal Code is required'],
  ['country', 'Country is required'],
]

const FedexFreightAccountSection = ({ locationDetail, changeValue, setLocationDetail, token, dispatch }) => {
  const [testingStandard, setTestingStandard] = useState(false)
  const [testingDirect, setTestingDirect] = useState(false)
  const [standardFieldErrors, setStandardFieldErrors] = useState({})
  const [directFieldErrors, setDirectFieldErrors] = useState({})

  const accountType = locationDetail?.fedex_account_type || 'third_party'

  const showAlert = (message, isError) => {
    dispatch({
      type: 'ALERT_MESSAGE',
      payload: {
        alertMessage: message,
        showAlertMessage: true,
        alertMessageType: isError ? 'error' : 'success',
      },
    })
  }

  // Test Connection needs enough info to actually attempt a test, independent of
  // whether the Freight Direct block is optional at Save time (backend allows saving
  // it blank if the account number itself is left empty).
  const validateForTest = (prefix, accountNumberFieldName) => {
    const errors = {}
    if (!String(locationDetail?.[accountNumberFieldName] || '').trim()) {
      errors[accountNumberFieldName] = 'FedEx Freight Account Number is required'
    }
    REQUIRED_TEST_FIELDS.forEach(([suffix, message]) => {
      const key = `fedex_${prefix}_${suffix}`
      if (!String(locationDetail?.[key] || '').trim()) {
        errors[key] = message
      }
    })
    return errors
  }

  const clearFieldError = (setFieldErrors, fieldName) => {
    setFieldErrors(prev => {
      if (!prev[fieldName]) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }

  const testAccount = async (prefix, accountNumberFieldName, setLoading, setFieldErrors) => {
    const errors = validateForTest(prefix, accountNumberFieldName)
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) {
      showAlert('Please fill in all required fields before testing the connection.', true)
      return
    }

    setLoading(true)
    try {
      const payload = {
        account_type: locationDetail?.[`fedex_${prefix}_test_type`] || 'shipper',
        shipping_account_number: locationDetail?.[accountNumberFieldName],
        third_party_account: locationDetail?.[`fedex_${prefix}_third_party_account_number`],
        account_name: locationDetail?.[`fedex_${prefix}_account_name`],
        address: locationDetail?.[`fedex_${prefix}_address`],
        city: locationDetail?.[`fedex_${prefix}_city`],
        state: locationDetail?.[`fedex_${prefix}_state`],
        zip: locationDetail?.[`fedex_${prefix}_zip`],
        country: locationDetail?.[`fedex_${prefix}_country`],
      }
      const { data } = await axios.post(
        `${process.env.REACT_APP_ENITURE_API_URL}/test_location_fedex_account`,
        payload,
        { headers: { authorization: `Bearer ${token}` } }
      )
      showAlert(data.message || (data.error ? 'Test connection failed.' : 'Test connection successful.'), !!data.error)
    } catch (error) {
      showAlert('Test connection failed.', true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
        <strong>FedEx Freight</strong>
      </Col>
      <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24}>
        <Form.Item className='mb-2'>
          <Radio.Group
            value={accountType}
            onChange={e => setLocationDetail({ ...locationDetail, fedex_account_type: e.target.value })}
          >
            <Radio style={{ display: 'block', marginBottom: 8 }} value='third_party'>Use my third-party account number</Radio>
            <Radio style={{ display: 'block' }} value='location_specific'>Use a location-specific account number</Radio>
          </Radio.Group>
        </Form.Item>
      </Col>

      {accountType === 'location_specific' && (
        <>
          <FedexFreightAccountFieldset
            title='Standard LTL Freight Account Information'
            keyPrefix='standard'
            accountNumberFieldName='fedex_account_number'
            locationDetail={locationDetail}
            changeValue={e => { changeValue(e); clearFieldError(setStandardFieldErrors, e.target.name) }}
            fieldErrors={standardFieldErrors}
          />
          <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24} style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              size='small'
              htmlType='button'
              loading={testingStandard}
              onClick={() => testAccount('standard', 'fedex_account_number', setTestingStandard, setStandardFieldErrors)}
            >
              Test Connection
            </Button>
          </Col>

          <FedexFreightAccountFieldset
            title='FedEx Freight Direct Account Information'
            keyPrefix='direct'
            accountNumberFieldName='fedex_direct_account_number'
            showFreightDirectLink
            locationDetail={locationDetail}
            changeValue={e => { changeValue(e); clearFieldError(setDirectFieldErrors, e.target.name) }}
            fieldErrors={directFieldErrors}
          />
          <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={24} style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              size='small'
              htmlType='button'
              loading={testingDirect}
              onClick={() => testAccount('direct', 'fedex_direct_account_number', setTestingDirect, setDirectFieldErrors)}
            >
              Test Connection
            </Button>
          </Col>
        </>
      )}
    </>
  )
}

export default FedexFreightAccountSection
